<!-- Bandeau d'introduction -->
<section class="relative overflow-hidden bg-white text-(--text-body)">
  <div class="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-4 lg:px-8">
    <div class="grid items-center gap-6 lg:grid-cols-[1fr_auto]">
      <div class="relative pl-6">
        <div class="absolute inset-y-0 left-0 w-1">
          <div class="h-1/3 w-full bg-(--cmr-vert)"></div>
          <div class="h-1/3 w-full bg-(--cmr-rouge)"></div>
          <div class="h-1/3 w-full bg-(--cmr-jaune)"></div>
        </div>
        <div class="text-xs font-bold tracking-[.12em] text-(--text-muted) uppercase">{{ 'pages.cartography.eyebrow' | translate }}</div>
        <h1 class="mt-3 max-w-3xl text-3xl font-extrabold leading-tight text-(--pnud-bleu) sm:text-4xl">
          {{ 'pages.cartography.title' | translate }}
        </h1>
        <p class="mt-4 max-w-2xl text-[15px] leading-relaxed text-(--text-body)">
          {{ 'pages.cartography.subtitle' | translate }}
        </p>
      </div>

      <!-- Visuel promotionnel "Les 7 points cardinaux" : purement illustratif (rayonnement national),
           distinct de la carte interactive ci-dessous qui reste la seule source de données réelles. -->
      <img ngSrc="assets/images/pic/visuel-youth-connekt-2.webp" [alt]="'pages.cartography.heroImageAlt' | translate"
        width="1600" height="1600" priority
        class="mx-auto h-auto w-full max-w-72 rounded-md shadow-(--shadow-raised) sm:max-w-80" />
    </div>
  </div>
</section>

<section class="bg-white py-8">
  <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <!-- Statistiques -->
      <div class="grid gap-4 sm:grid-cols-3">
        <div class="box-border rounded-md border-l-4 border-(--cmr-vert) bg-white p-4 shadow-(--shadow-card)">
          <div class="text-2xl font-extrabold text-(--marine)">{{ statsTotal() }}</div>
          <div class="mt-0.5 text-[12.5px] text-(--text-muted)">{{ 'pages.cartography.statMappedStructures' | translate }}</div>
        </div>
        <div class="box-border rounded-md border-l-4 border-(--cmr-rouge) bg-white p-4 shadow-(--shadow-card)">
          <div class="text-2xl font-extrabold text-(--marine)">{{ statsRegionsCount() }}</div>
          <div class="mt-0.5 text-[12.5px] text-(--text-muted)">{{ 'pages.cartography.statRegionsCovered' | translate }}</div>
        </div>
        <div class="box-border rounded-md border-l-4 border-(--cmr-jaune) bg-white p-4 shadow-(--shadow-card)">
          <div class="text-2xl font-extrabold text-(--marine)">{{ statsSectorsCount() }}</div>
          <div class="mt-0.5 text-[12.5px] text-(--text-muted)">{{ 'pages.cartography.statSectors' | translate }}</div>
        </div>
      </div>

      <!-- Recherche + bascule carte/liste -->
      <form (submit)="$event.preventDefault(); applyQuery()" class="mt-5 flex flex-wrap items-center gap-3">
        <label for="eco-search" class="sr-only">{{ 'pages.cartography.searchLabel' | translate }}</label>
        <input id="eco-search" type="search" [placeholder]="'pages.cartography.searchPlaceholder' | translate" [value]="query()"
          (input)="query.set($any($event.target).value)"
          class="box-border h-11 min-w-0 flex-1 rounded-sm border border-(--border-input) bg-white px-3.5 text-[14.5px] text-(--text-body) outline-none focus:border-(--brand-primary) focus:shadow-(--focus-ring)" />
        <button type="submit"
          class="box-border h-11 shrink-0 cursor-pointer rounded-sm border-0 bg-(--brand-primary) px-5 text-[14px] font-bold text-white transition hover:bg-(--brand-primary-hover)">
          {{ 'pages.cartography.searchButton' | translate }}
        </button>

        <div class="flex overflow-hidden rounded-sm border border-(--border-strong)" role="group" [attr.aria-label]="'pages.cartography.viewModeAriaLabel' | translate">
          <button type="button" (click)="setViewMode('carte')" [attr.aria-pressed]="viewMode() === 'carte'"
            [class]="viewMode() === 'carte' ? 'bg-(--pnud-bleu) text-white' : 'bg-white text-(--text-body) hover:bg-(--surface-subtle)'"
            class="box-border inline-flex h-11 cursor-pointer items-center gap-1.5 border-0 px-4 text-[13.5px] font-bold transition-colors">
            <i class="pi pi-map" aria-hidden="true"></i> {{ 'pages.cartography.mapViewButton' | translate }}
          </button>
          <button type="button" (click)="setViewMode('liste')" [attr.aria-pressed]="viewMode() === 'liste'"
            [class]="viewMode() === 'liste' ? 'bg-(--pnud-bleu) text-white' : 'bg-white text-(--text-body) hover:bg-(--surface-subtle)'"
            class="box-border inline-flex h-11 cursor-pointer items-center gap-1.5 border-0 border-l border-(--border-strong) px-4 text-[13.5px] font-bold transition-colors">
            <i class="pi pi-list" aria-hidden="true"></i> {{ 'pages.cartography.listViewButton' | translate }}
          </button>
        </div>
      </form>

      <div class="mt-6 grid gap-6 lg:grid-cols-[260px_1fr]">
        <!-- Filtres -->
        <aside class="flex flex-col gap-6">
          <div>
            <div class="mb-2.5 flex items-center justify-between">
              <h2 class="m-0 text-[11px] font-extrabold tracking-widest text-(--text-faint) uppercase">{{ 'pages.cartography.structureTypeTitle' | translate }}</h2>
              <button type="button" (click)="resetFilters()"
                class="cursor-pointer border-0 bg-transparent p-0 text-[12px] font-bold text-(--text-link) hover:underline">
                {{ 'pages.cartography.resetButton' | translate }}
              </button>
            </div>
            <div class="flex flex-col gap-1.5">
              <button type="button" (click)="setType('')" [attr.aria-pressed]="typeFilter() === ''"
                [class]="typeFilter() === '' ? 'border-(--pnud-bleu) bg-(--surface-primary-tint)' : 'border-(--border-subtle) bg-white hover:border-(--border-strong)'"
                class="box-border flex cursor-pointer items-center justify-between rounded-sm border px-3 py-2 text-left text-[13.5px] font-bold text-(--text-body) transition-colors">
                <span class="flex items-center gap-2">
                  <span class="h-2.5 w-2.5 rounded-sm bg-(--gris-500)" aria-hidden="true"></span>
                  {{ 'pages.cartography.allStructuresOption' | translate }}
                </span>
              </button>
              @for (type of typeFilterOrder; track type) {
                <button type="button" (click)="setType(type)" [attr.aria-pressed]="typeFilter() === type"
                  [class]="typeFilter() === type ? 'border-(--pnud-bleu) bg-(--surface-primary-tint)' : 'border-(--border-subtle) bg-white hover:border-(--border-strong)'"
                  class="box-border flex cursor-pointer items-center justify-between rounded-sm border px-3 py-2 text-left text-[13.5px] font-bold text-(--text-body) transition-colors">
                  <span class="flex items-center gap-2">
                    <span class="h-2.5 w-2.5 rounded-sm" [style.background]="typeColors[type]" aria-hidden="true"></span>
                    {{ organizationTypeLabel(type) }}
                  </span>
                </button>
              }
            </div>
          </div>

          <div>
            <label for="eco-region" class="mb-2 block text-[11px] font-extrabold tracking-widest text-(--text-faint) uppercase">{{ 'pages.cartography.regionLabel' | translate }}</label>
            <select id="eco-region" [value]="regionFilter()" (change)="setRegionFilter($any($event.target).value)"
              class="box-border h-10 w-full rounded-sm border border-(--border-input) bg-white px-2.5 text-[13.5px] text-(--text-body) outline-none focus:border-(--brand-primary) focus:shadow-(--focus-ring)">
              <option value="">{{ 'pages.cartography.allRegionsOption' | translate }}</option>
              @for (region of regionOptions(); track region[1]) {
                <option [value]="region[1]">{{ ('regions.' + region[1]) | translate }}</option>
              }
            </select>
          </div>

          <div>
            <label for="eco-sector" class="mb-2 block text-[11px] font-extrabold tracking-widest text-(--text-faint) uppercase">{{ 'pages.cartography.sectorLabel' | translate }}</label>
            <select id="eco-sector" [value]="sectorFilter()" (change)="setSectorFilter($any($event.target).value)"
              class="box-border h-10 w-full rounded-sm border border-(--border-input) bg-white px-2.5 text-[13.5px] text-(--text-body) outline-none focus:border-(--brand-primary) focus:shadow-(--focus-ring)">
              <option value="">{{ 'pages.cartography.allSectorsOption' | translate }}</option>
              @for (sector of sectorOptions(); track sector[1]) {
                <option [value]="sector[1]">{{ ('sectors.' + sector[1]) | translate }}</option>
              }
            </select>
          </div>

          <div>
            <h2 class="mb-2.5 text-[11px] font-extrabold tracking-widest text-(--text-faint) uppercase">{{ 'pages.cartography.legendTitle' | translate }}</h2>
            <ul class="m-0 flex list-none flex-col gap-1.5 p-0 text-[13px] text-(--text-body)">
              @for (type of typeFilterOrder; track type) {
                <li class="flex items-center gap-2">
                  <span class="h-2.5 w-2.5 shrink-0 rounded-full" [style.background]="typeColors[type]" aria-hidden="true"></span>
                  {{ organizationTypeLabel(type) }}
                </li>
              }
            </ul>
          </div>
        </aside>

        <!-- Carte + résultats -->
        <div>
          @if (error()) {
            <p class="mb-4 rounded-md border border-[#FECACA] bg-[#FFF1F2] px-4 py-3 text-[13.5px] font-semibold text-(--state-danger)" role="alert">
              {{ 'pages.cartography.loadError' | translate }}
            </p>
          }

          <div [class.hidden]="viewMode() !== 'carte'">
            <!-- isolate : Leaflet fixe lui-même z-index:1000 sur ses contrôles, ce qui sans contexte
                 d'empilement propre passerait au-dessus du header sticky (z-50) une fois scrollé. -->
            <div class="relative isolate overflow-hidden rounded-md border border-(--border-subtle) bg-[#EAF3FF] shadow-(--shadow-card)">
              <div class="absolute left-3 top-3 z-10 rounded-sm bg-white/95 px-2.5 py-1 text-[12px] font-bold text-(--marine) shadow-(--shadow-card)">
                {{ 'pages.cartography.structureCountBadge' | translate: { count: totalMatches() } }}
              </div>
              <div #mapContainer class="relative z-[1] h-120 w-full" role="application" [attr.aria-label]="'pages.cartography.mapAriaLabel' | translate"></div>
              @if (loading()) {
                <div class="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center bg-white/35 text-sm font-semibold text-(--marine)" aria-live="polite">
                  {{ 'pages.cartography.updatingMap' | translate }}
                </div>
              }
              @if (!mapReady() && !mapUnavailable()) {
                <div class="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center bg-[#EAF3FF] text-sm font-semibold text-(--text-muted)">
                  {{ 'pages.cartography.initializingMap' | translate }}
                </div>
              }
              @if (mapUnavailable()) {
                <div class="absolute inset-0 z-[2] flex items-center justify-center bg-[#FFF1F2] px-6 text-center text-sm font-semibold text-[#BE123C]" role="alert">
                  {{ 'pages.cartography.mapUnavailable' | translate }}
                </div>
              }
            </div>
            @if (mapUnavailable() && hasMappableRegions()) {
              <div class="mt-3 rounded-md border border-(--border-subtle) bg-white p-4 shadow-(--shadow-card)">
                <div class="grid min-h-56 grid-cols-2 gap-2 md:grid-cols-5">
                  @for (pin of fallbackPins(); track pin.regionCode) {
                    <button type="button" (click)="focusRegion(pin.regionCode)" class="rounded-sm border border-(--border-subtle) bg-(--surface-primary-tint) px-3 py-2 text-left text-[13px] font-bold text-(--brand-primary)">
                      {{ pin.label }} · {{ pin.count }}
                    </button>
                  }
                </div>
              </div>
            }
            <p class="mt-2 text-[12px] text-(--text-faint)">
              {{ 'pages.cartography.mapLegendNote' | translate }}
              @if (!hasMappableRegions() && totalMatches() > 0) {
                {{ 'pages.cartography.noMappableRegionsNote' | translate }}
              }
            </p>
          </div>

          @if (truncated()) {
            <p class="mt-3 text-[12px] text-(--text-faint)" role="status">
              {{ 'pages.cartography.truncatedNotice' | translate: { shown: allMatches().length, total: totalMatches() } }}
            </p>
          }

          <div class="mt-6 flex items-baseline justify-between gap-3">
            <h2 class="m-0 text-[15px] font-extrabold text-(--marine) uppercase">{{ 'pages.cartography.resultsTitle' | translate: { count: filteredResults().length } }}</h2>
            <span class="text-[12.5px] text-(--text-faint)">
              {{ regionFilter() ? ('pages.cartography.regionFilterActive' | translate: { region: regionLabel(regionFilter()) }) : ('pages.cartography.wholeTerritory' | translate) }}
            </span>
          </div>

          <div class="mt-3 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
            @for (point of pagedResults(); track $index) {
              <article
                class="box-border block cursor-pointer rounded-md border border-(--border-subtle) border-l-4 bg-white p-4 shadow-(--shadow-card) transition hover:-translate-y-0.5 hover:shadow-(--shadow-raised)"
                [style.borderLeftColor]="colorFor(pointTypeKey(point))">
                <div class="flex items-start gap-3">
                  @if (point.imageUrl) {
                    <img [src]="point.imageUrl" [alt]="point.label" class="h-9 w-9 shrink-0 rounded-full object-cover" />
                  } @else {
                    <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-extrabold text-white"
                      [style.background]="colorFor(pointTypeKey(point))">
                      {{ initials(point.label) }}
                    </span>
                  }
                  <div class="min-w-0">
                    <h3 class="m-0 text-[14.5px] font-extrabold leading-snug text-(--marine)">{{ point.label }}</h3>
                    <p class="m-0 text-[12.5px] text-(--text-muted)">{{ point.city }} · {{ regionLabel(regionFor(point)) }}</p>
                  </div>
                </div>
                @if (point.shortDescription) {
                  <p class="mt-2.5 line-clamp-2 text-[13px] leading-relaxed text-(--text-body)">{{ point.shortDescription }}</p>
                }
                <div class="mt-3 flex flex-wrap gap-1.5">
                  <span class="inline-flex h-6 items-center rounded-full px-2.5 text-[11.5px] font-bold"
                    [style.background]="'color-mix(in srgb, ' + colorFor(pointTypeKey(point)) + ' 14%, white)'"
                    [style.color]="colorFor(pointTypeKey(point))">
                    {{ organizationTypeLabel(pointTypeKey(point)) }}
                  </span>
                  @if (point.sectorCode; as sectorCode) {
                    <span class="inline-flex h-6 items-center rounded-full bg-(--gris-100) px-2.5 text-[11.5px] font-bold text-(--text-muted)">
                      {{ sectorLabel(sectorCode) }}
                    </span>
                  }
                </div>
                <div class="mt-3 flex flex-wrap gap-2">
                  @if (point.pointType === 'ORGANIZATION' && point.profileId) {
                    <a [routerLink]="['/organizations', point.profileId]" class="text-[12px] font-bold text-(--brand-primary) underline-offset-2 hover:underline">Voir le profil</a>
                  }
                  @if (point.phone; as phone) {
                    <a [href]="'tel:' + phoneHref(phone)" class="text-[12px] font-bold text-(--brand-primary) underline-offset-2 hover:underline">Appeler</a>
                    <a [href]="whatsappHref(phone)" target="_blank" rel="noopener noreferrer" class="text-[12px] font-bold text-(--brand-primary) underline-offset-2 hover:underline">WhatsApp</a>
                  }
                </div>
              </article>
            } @empty {
              <p class="col-span-full m-0 text-center text-[13.5px] text-(--text-muted)">
                {{ 'pages.cartography.noResults' | translate }}
              </p>
            }
          </div>

          @if (totalPages() > 1) {
            <div class="mt-5 flex items-center justify-center gap-3">
              <button type="button" (click)="previousPage()" [disabled]="page() === 0"
                class="box-border inline-flex h-9 cursor-pointer items-center justify-center rounded-sm border border-(--border-strong) bg-white px-3.5 text-[13px] font-bold text-(--text-body) disabled:cursor-not-allowed disabled:opacity-50">
                {{ 'pages.cartography.previousButton' | translate }}
              </button>
              <span class="text-[13px] text-(--text-muted)">{{ 'pages.cartography.pageOf' | translate: { current: page() + 1, total: totalPages() } }}</span>
              <button type="button" (click)="nextPage()" [disabled]="page() + 1 >= totalPages()"
                class="box-border inline-flex h-9 cursor-pointer items-center justify-center rounded-sm border border-(--border-strong) bg-white px-3.5 text-[13px] font-bold text-(--text-body) disabled:cursor-not-allowed disabled:opacity-50">
                {{ 'pages.cartography.nextButton' | translate }}
              </button>
            </div>
          }
        </div>
      </div>
  </div>
</section>
